/*
 * <int-gallery> — native Shopify product gallery (alternative to the Cloudinary widget).
 * See snippets/int-gallery.liquid and assets/component-int-gallery.css
 *
 * Behaviour:
 *  - Click a thumbnail (or use the stage prev/next arrows / swipe) to change the main image.
 *  - Click the main image to zoom; move the cursor to pan; click again to zoom out.
 *  - Vertical thumbnail strip scrolls via the up/down arrows (shown only when it overflows).
 *
 * Built as a custom element so it re-initialises automatically whenever the section
 * markup is re-rendered (e.g. on a variant change).
 */
if (!customElements.get('int-gallery')) {
  customElements.define(
    'int-gallery',
    class IntGallery extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.slides = Array.from(this.querySelectorAll('.int-gallery__slide'));
        this.thumbs = Array.from(this.querySelectorAll('.int-gallery__thumb'));
        this.track = this.querySelector('.int-gallery__thumb-track');
        this.stage = this.querySelector('.int-gallery__stage');
        this.thumbPrev = this.querySelector('.int-gallery__thumb-nav--prev');
        this.thumbNext = this.querySelector('.int-gallery__thumb-nav--next');
        this.stagePrev = this.querySelector('.int-gallery__stage-nav--prev');
        this.stageNext = this.querySelector('.int-gallery__stage-nav--next');

        this.index = 0;
        this.count = this.slides.length;
        this.zoomed = false;

        if (!this.count) return;

        this.bindEvents();
        this.updateThumbNav();
      }

      bindEvents() {
        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => this.setActive(Number(thumb.dataset.index)));
        });

        if (this.stagePrev) this.stagePrev.addEventListener('click', () => this.step(-1));
        if (this.stageNext) this.stageNext.addEventListener('click', () => this.step(1));

        if (this.thumbPrev) this.thumbPrev.addEventListener('click', () => this.scrollThumbs(-1));
        if (this.thumbNext) this.thumbNext.addEventListener('click', () => this.scrollThumbs(1));
        if (this.track) {
          this.track.addEventListener('scroll', () => this.updateThumbNav(), { passive: true });
        }

        // Zoom / pan on the main stage.
        this.stage.addEventListener('click', (event) => this.toggleZoom(event));
        this.stage.addEventListener('mousemove', (event) => this.pan(event), { passive: true });
        this.stage.addEventListener('mouseleave', () => this.resetZoom());

        // Touch swipe on the main stage.
        this.stage.addEventListener(
          'touchstart',
          (event) => {
            this.touchStartX = event.changedTouches[0].clientX;
          },
          { passive: true }
        );
        this.stage.addEventListener(
          'touchend',
          (event) => {
            const deltaX = event.changedTouches[0].clientX - this.touchStartX;
            if (Math.abs(deltaX) > 40) this.step(deltaX < 0 ? 1 : -1);
          },
          { passive: true }
        );

        window.addEventListener('resize', () => this.updateThumbNav(), { passive: true });
      }

      setActive(index) {
        if (index === this.index || index < 0 || index >= this.count) return;
        this.resetZoom();

        this.slides[this.index].classList.remove('is-active');
        this.thumbs[this.index].classList.remove('is-active');
        this.thumbs[this.index].removeAttribute('aria-current');

        this.index = index;

        this.slides[index].classList.add('is-active');
        this.thumbs[index].classList.add('is-active');
        this.thumbs[index].setAttribute('aria-current', 'true');

        this.scrollThumbIntoView(index);
      }

      step(direction) {
        let next = this.index + direction;
        if (next < 0) next = this.count - 1;
        if (next >= this.count) next = 0;
        this.setActive(next);
      }

      scrollThumbIntoView(index) {
        const thumb = this.thumbs[index];
        if (thumb) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }

      scrollThumbs(direction) {
        if (!this.track) return;
        // Thumbnails scroll vertically on desktop, horizontally on mobile.
        if (this.track.scrollWidth - this.track.clientWidth > 1) {
          this.track.scrollBy({ left: direction * this.track.clientWidth * 0.8, behavior: 'smooth' });
        } else {
          this.track.scrollBy({ top: direction * this.track.clientHeight * 0.8, behavior: 'smooth' });
        }
      }

      updateThumbNav() {
        if (!this.track || !this.thumbPrev || !this.thumbNext) return;

        const horizontal = this.track.scrollWidth - this.track.clientWidth > 1;
        const vertical = this.track.scrollHeight - this.track.clientHeight > 1;
        const overflows = horizontal || vertical;
        this.thumbPrev.hidden = !overflows;
        this.thumbNext.hidden = !overflows;
        if (!overflows) return;

        const start = horizontal ? this.track.scrollLeft : this.track.scrollTop;
        const viewport = horizontal ? this.track.clientWidth : this.track.clientHeight;
        const total = horizontal ? this.track.scrollWidth : this.track.scrollHeight;
        this.thumbPrev.disabled = start <= 1;
        this.thumbNext.disabled = start + viewport >= total - 1;
      }

      toggleZoom(event) {
        if (event.target.closest('.int-gallery__stage-nav')) return;
        if (this.zoomed) {
          this.resetZoom();
          return;
        }
        this.zoomed = true;
        this.slides[this.index].classList.add('is-zoomed');
        this.pan(event);
      }

      pan(event) {
        if (!this.zoomed) return;
        const rect = this.stage.getBoundingClientRect();
        const x = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
        const y = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));
        const image = this.slides[this.index].querySelector('.int-gallery__image');
        if (image) image.style.transformOrigin = x + '% ' + y + '%';
      }

      resetZoom() {
        if (!this.zoomed) return;
        this.zoomed = false;
        const slide = this.slides[this.index];
        slide.classList.remove('is-zoomed');
        const image = slide.querySelector('.int-gallery__image');
        if (image) image.style.transformOrigin = '';
      }
    }
  );
}
